import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Loader2 } from 'lucide-react'
import { useIsMobile } from '@/hooks/use-mobile'
import { useAccessTier } from '@/hooks/useAccessTier'
import { useMyOrgRole } from '@/hooks/useOrganization'
import { takeOnboardingReturnTo } from '@/utils/onboardingReturn'
import { useDebouncedAutosave } from '@/hooks/useDebouncedAutosave'
import { AttestationIncompleteError, attestationService } from '@/services/attestationService'
import { AttestationScreenDesktop } from './AttestationScreenDesktop'
import { AttestationScreenMobile } from './AttestationScreenMobile'
import { InstitutionalSignupScreen, KybPendingPanel } from './institutional'
import { getDefaultFormData, type AttestationScreenBaseProps } from './AttestationScreenBase'
import type { AttestationFormData } from '@/types/attestation'
import type { AccountType } from '@/screens/signup/SignupTypeSelectionScreen'

const ACCOUNT_TYPE_STORAGE_KEY = 'signup_account_type'

export { ACCOUNT_TYPE_STORAGE_KEY }

/** Autosave quiet period for the individual form. */
const AUTOSAVE_DELAY_MS = 1200

/** Per-field copy for the names the server can report in `missing_fields`. */
const MISSING_FIELD_COPY: Record<string, string> = {
  affirmation: 'You must affirm the statement above before submitting.',
  country: 'Select the country you are resident in.',
  attestation_year:
    'The attestation year on your saved draft is out of range. Reload the page and try again.',
  email: 'We could not determine the email for your account. Reload and try again.',
}

/**
 * The individual form's local names vs. the server's.
 *
 * `market_insights_opt_in` is this form's name for what the API calls
 * `subscribe_to_newsletter`. Mapping here rather than renaming the form field
 * keeps the change out of the two presentational components.
 */
const toDraftPayload = (formData: AttestationFormData) => ({
  attestation_year: formData.attestation_year,
  individual_income_test: formData.individual_income_test,
  joint_income_test: formData.joint_income_test,
  net_worth_test: formData.net_worth_test,
  country: formData.country,
  affirmation: formData.affirmation,
  subscribe_to_newsletter: formData.market_insights_opt_in,
})

/**
 * AttestationScreen — the fork in the signup funnel.
 *
 * `individual` → the income / net-worth / affirmation form, now backed by the
 * draft/submit endpoints so a half-finished form survives a reload.
 * `institution` → the multi-step institutional wizard.
 *
 * 🔴 Both branches must terminate in a SUBMITTED attestation. `ProtectedRoute`
 * gates on `GET /attestations/status`, which is true only for a COMPLETE row —
 * a branch that finishes without submitting (as the institutional one used to,
 * creating an organization and no attestation at all) sends the user round an
 * infinite redirect loop that nothing in the UI can break out of.
 */
const AttestationScreen = () => {
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const { status: attestationStatus, refresh: refreshAccessTier } = useAccessTier()
  // Already normalized to `null` at the service boundary when the backend
  // omits the key.
  const pendingAttestation = attestationStatus?.pending_attestation ?? null

  // 🔴 `pending_attestation` is INHERITED through membership, so an invited
  // MEMBER of a pending organization lands here too — and a MEMBER may not
  // drive verification: the KYB link and domain-claim endpoints answer 403 for
  // them. Worse, the wizard's resume looks for an OWNER/ADMIN membership, finds
  // none, and falls through to its pre-organization path, where typing a name
  // would mint a SECOND organization for someone who already belongs to one.
  // Members get the pending panel, which has the role gating and the "an owner
  // or admin must do this" message.
  const { canManage, isLoading: roleLoading } = useMyOrgRole(
    pendingAttestation?.organization_id ?? '',
  )
  const [accountType, setAccountType] = useState<AccountType | null>(null)
  const [formData, setFormData] = useState<AttestationFormData>(getDefaultFormData)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [isCheckingStatus, setIsCheckingStatus] = useState(true)

  /**
   * Whether the user has asked to go back and change something from the
   * terminal pending panel. Local, and deliberately not derived from the
   * server: the server still says "pending", which is correct — the user just
   * wants the phase-2 steps in front of them again.
   */
  const [editingPhaseTwo, setEditingPhaseTwo] = useState(false)

  // Two refs, per the pattern in VerifyDomainScreen: the hydration guard has to
  // survive a StrictMode remount, and `mountedRef` is tracked separately because
  // an effect-scoped flag is already false by the time the one in-flight request
  // resolves, so every result would be silently dropped.
  const mountedRef = useRef(true)
  const hydratedRef = useRef(false)
  const draftIdRef = useRef<string | null>(null)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  // 🔴 The server-first check is the ACCESS TIER's, not this screen's.
  //
  // This screen used to run its own `GET /attestations/status` on mount, with
  // its own failure policy — it failed OPEN (proceed into the funnel) while
  // `App.tsx` failed CLOSED. Two readers, two policies, and a transient error
  // could put a user with a submitted attestation back at the start of phase
  // 1, where a fresh draft would SUPERSEDE the row they were waiting on.
  //
  // `AccessTierProvider` is now the single reader and it fails closed. Its
  // `status` is `null` until it answers, which is what holds the loading state
  // below — so nothing here, including the `'individual'` localStorage
  // default, can route the user before the server has spoken.
  useEffect(() => {
    // Get account type from localStorage (set during signup)
    const storedAccountType = localStorage.getItem(ACCOUNT_TYPE_STORAGE_KEY) as AccountType | null
    setAccountType(storedAccountType || 'individual') // Default to individual if not set
  }, [])

  // Resume an abandoned individual draft.
  useEffect(() => {
    if (accountType === null) return
    if (accountType === 'institution') {
      // The wizard runs its own, much larger, resume.
      setIsCheckingStatus(false)
      return
    }
    if (hydratedRef.current) return
    hydratedRef.current = true

    const hydrate = async () => {
      try {
        const draft = await attestationService.getDraft()
        if (draft && mountedRef.current) {
          draftIdRef.current = draft.attestation_id
          setFormData(prev => ({
            ...prev,
            attestation_year: draft.attestation_year ?? prev.attestation_year,
            individual_income_test: draft.individual_income_test,
            joint_income_test: draft.joint_income_test,
            net_worth_test: draft.net_worth_test,
            country: draft.country ?? '',
            market_insights_opt_in: draft.subscribe_to_newsletter,
            affirmation: draft.affirmation,
          }))
        } else if (draft) {
          draftIdRef.current = draft.attestation_id
        }
      } catch (err) {
        // A draft that will not load is not worth blocking signup over — the
        // form is short and can simply be filled in again.
        console.error('Could not load attestation draft:', err)
      } finally {
        if (mountedRef.current) setIsCheckingStatus(false)
      }
    }

    void hydrate()
  }, [accountType])

  const persistDraft = useCallback(async (snapshot: AttestationFormData) => {
    const draft = await attestationService.saveDraft(toDraftPayload(snapshot))
    draftIdRef.current = draft.attestation_id
  }, [])

  const autosave = useDebouncedAutosave({
    value: formData,
    save: persistDraft,
    delay: AUTOSAVE_DELAY_MS,
    // Never autosave before the resume has finished, or the empty initial form
    // would be written straight over the draft it is about to load.
    enabled: accountType === 'individual' && !isCheckingStatus,
  })

  const handleFormDataChange = (updates: Partial<AttestationFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }))
    // Clear error when user starts typing
    if (error) setError('')
    setFieldErrors(prev => {
      const keys = Object.keys(updates)
      if (!keys.some(key => key in prev)) return prev
      const next = { ...prev }
      for (const key of keys) delete next[key]
      return next
    })
  }

  const handleAttestationSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setFieldErrors({})

    try {
      // Push anything still sitting in the debounce, then submit what the server
      // now holds. `flush` resolves after the write lands, so the draft id below
      // is populated by the time it is read.
      await autosave.flush()

      let attestationId = draftIdRef.current
      if (!attestationId) {
        // Autosave was disabled or the write failed; create the draft directly
        // rather than failing the submit.
        const draft = await attestationService.saveDraft(toDraftPayload(formData))
        attestationId = draft.attestation_id
        draftIdRef.current = attestationId
      }

      await attestationService.submitDraft(attestationId)

      // Clear account type from localStorage after successful attestation
      localStorage.removeItem(ACCOUNT_TYPE_STORAGE_KEY)
      // Full reload, so the route guards re-read the server's onboarding signal
      window.location.href = '/profile-preferences'
    } catch (err) {
      if (!mountedRef.current) return
      if (err instanceof AttestationIncompleteError) {
        const mapped: Record<string, string> = {}
        for (const name of err.missingFields) {
          mapped[name] = MISSING_FIELD_COPY[name] || `${name} is required.`
        }
        setFieldErrors(mapped)
      } else {
        setError(
          err instanceof Error ? err.message : 'Failed to submit attestation. Please try again.',
        )
      }
    } finally {
      if (mountedRef.current) setLoading(false)
    }
  }

  const handleInstitutionalComplete = useCallback(() => {
    // Clear account type from localStorage after completion
    localStorage.removeItem(ACCOUNT_TYPE_STORAGE_KEY)
    window.location.href = '/profile-preferences'
  }, [])

  /**
   * Phase 1 submitted — the user is in.
   *
   * 🔴 `refresh()` then `navigate()`, not `window.location.href`. The access
   * tier is context now, so re-reading the status updates the route guards and
   * every gated surface in place; a full-document reload would work too but
   * would throw away the app state the user is about to come back to. Await
   * the refresh first: navigating on a stale `anonymous` tier would have
   * `ProtectedRoute` bounce them straight back here.
   */
  const handlePhaseOneComplete = useCallback(async () => {
    localStorage.removeItem(ACCOUNT_TYPE_STORAGE_KEY)
    await refreshAccessTier()
    if (!mountedRef.current) return
    navigate(takeOnboardingReturnTo() ?? '/profile-preferences', { replace: true })
  }, [navigate, refreshAccessTier])

  /**
   * Phase 2 finished, or paused. Either way, back into the app.
   *
   * The refresh matters even when nothing was completed: the KYB may have gone
   * GREEN while the user was on the page, which promotes the attestation to
   * COMPLETE and upgrades them from `browse` to `full`.
   */
  const handlePhaseTwoDone = useCallback(async () => {
    await refreshAccessTier()
    if (!mountedRef.current) return
    navigate(takeOnboardingReturnTo() ?? '/', { replace: true })
  }, [navigate, refreshAccessTier])

  if (
    attestationStatus === null ||
    accountType === null ||
    (accountType === 'individual' && isCheckingStatus)
  ) {
    return (
      <div className="min-h-screen bg-[var(--background)] flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary-500" />
          <h2 className="text-xl font-semibold text-[var(--text)]">
            Checking attestation status...
          </h2>
        </motion.div>
      </div>
    )
  }

  // Server-first: a submitted attestation waiting on verification decides what
  // renders, before — and regardless of — any account-type branching.
  //
  // 🔴 This branch used to be a dead end: `KybPendingPanel`, always, because a
  // PENDING_VERIFICATION attestation could not get into the app. Onboarding is
  // two phases now, and this state means "phase 1 done". So:
  //
  //   still something to do  → PHASE 2 of the wizard
  //   nothing left to do     → the pending panel, with a way back into the app
  //
  // "Nothing left to do" is: the KYB is with Sumsub or already approved, AND
  // the domain precondition is satisfied. `email_domain_verified` from the
  // status endpoint is the PRECONDITION, not the raw column — the backend
  // reports it satisfied both for a verified claim and for an org that claimed
  // no domain at all, so an org with no claim is not told to chase one.
  if (pendingAttestation !== null) {
    // 🔴 An INDIVIDUAL pending attestation must not be shown the institutional
    // phase-2 wizard: there is no organization behind it, so the domain and
    // KYB steps have nothing to act on and `ensureOrganization` would mint a
    // stray organization. Individuals go DRAFT → COMPLETE and should never
    // reach this state, but "should never" is not "cannot".
    if (pendingAttestation.subject_type !== 'ORGANIZATION') {
      return (
        <KybPendingPanel
          pendingAttestation={pendingAttestation}
          onBrowse={() => navigate('/', { replace: true })}
        />
      )
    }

    // Hold rather than guess while the role is still loading: showing the
    // wizard to someone who turns out to be a MEMBER is the failure that
    // mints a stray organization.
    if (roleLoading) {
      return (
        <div className="min-h-screen bg-[var(--background)] flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center"
          >
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary-500" />
            <h2 className="text-xl font-semibold text-[var(--text)]">
              Checking attestation status...
            </h2>
          </motion.div>
        </div>
      )
    }

    const kybWithSumsub =
      pendingAttestation.kyb_status === 'pending_review' ||
      pendingAttestation.kyb_status === 'approved'
    const nothingActionable = kybWithSumsub && pendingAttestation.email_domain_verified

    // 🔴 `!editingPhaseTwo` belongs HERE, not on the outer branch. It used to
    // guard the whole block, so "Change my details" fell through to the
    // account-type branch below and re-opened PHASE ONE — where a fresh draft
    // would have superseded the submitted row the user is waiting on.
    if (!canManage || (nothingActionable && !editingPhaseTwo)) {
      return (
        <KybPendingPanel
          pendingAttestation={pendingAttestation}
          // A MEMBER is never offered the wizard: the endpoints behind it
          // answer 403 for them.
          onEdit={canManage ? () => setEditingPhaseTwo(true) : undefined}
          onBrowse={() => navigate('/', { replace: true })}
        />
      )
    }

    return (
      <InstitutionalSignupScreen
        // 🔴 Keyed on the phase. The wizard's resume effect is guarded by a ref
        // that survives a StrictMode remount, so a phase change has to be a
        // genuinely new component instance or it would keep phase 1's state.
        key="phase-two"
        phase="two"
        onComplete={handleInstitutionalComplete}
        onPhaseOneComplete={handlePhaseOneComplete}
        onPhaseTwoDone={handlePhaseTwoDone}
      />
    )
  }

  // Route based on account type
  if (accountType === 'institution') {
    return (
      <InstitutionalSignupScreen
        key="phase-one"
        phase="one"
        onComplete={handleInstitutionalComplete}
        onPhaseOneComplete={handlePhaseOneComplete}
        onPhaseTwoDone={handlePhaseTwoDone}
      />
    )
  }

  // If individual (or default), show individual attestation form
  const baseProps: AttestationScreenBaseProps = {
    formData,
    loading,
    error,
    fieldErrors,
    saveState: autosave.state,
    onFormDataChange: handleFormDataChange,
    onSubmit: handleAttestationSubmit,
  }

  // Component Split pattern - completely separate mobile and desktop rendering
  if (isMobile) {
    return <AttestationScreenMobile {...baseProps} />
  }

  return <AttestationScreenDesktop {...baseProps} />
}

export default AttestationScreen
